import {
  CheckSquare,
  FileImage,
  Grid3X3,
  List,
  RefreshCw,
  Square,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import drawingService from '../../services/drawingService';
import { DrawingBulkOperation, DrawingQuery, ProjectDrawing } from '../../types';
import LoadingSpinner from '../LoadingSpinner';
import Pagination from '../Pagination';
import DrawingCard from './DrawingCard';
import DrawingFilters from './DrawingFilters';
import DrawingFormDrawer from './DrawingFormDrawer';
import DrawingViewer from './DrawingViewer';

interface DrawingManagementProps {
  projectId?: string;
  projectName?: string;
  showProject?: boolean;
  compact?: boolean;
  onDrawingSelect?: (drawing: ProjectDrawing) => void;
}

const DrawingManagement: React.FC<DrawingManagementProps> = ({
  projectId,
  projectName,
  showProject = false,
  compact = false,
  onDrawingSelect,
}) => {
  const toast = useToast();

  // State
  const [drawings, setDrawings] = useState<ProjectDrawing[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedDrawings, setSelectedDrawings] = useState<Set<string>>(new Set());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDrawing, setEditingDrawing] = useState<ProjectDrawing | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedDrawing, setSelectedDrawing] = useState<ProjectDrawing | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDrawings, setTotalDrawings] = useState(0);

  // Query and filters
  const [query, setQuery] = useState<DrawingQuery>(() => ({
    page: 1,
    limit: compact ? 8 : 20,
    sort_by: 'uploaded_at',
    sort_order: 'desc',
    ...(projectId && { project_id: projectId }),
  }));

  // Configuration options
  const [projects, setProjects] = useState<Array<{ value: string; label: string }>>([]);
  const [categories, setCategories] = useState<
    Array<{ value: string; label: string; color: string }>
  >([]);
  const [statuses, setStatuses] = useState<Array<{ value: string; label: string; color: string }>>(
    []
  );
  const [disciplines, setDisciplines] = useState<Array<{ value: string; label: string }>>([]);
  const [phases, setPhases] = useState<Array<{ value: string; label: string }>>([]);
  const [sortOptions, setSortOptions] = useState<Array<{ value: string; label: string }>>([]);

  // Load data
  const loadDrawings = useCallback(
    async (searchQuery?: DrawingQuery) => {
      try {
        setLoading(true);
        const queryToUse = searchQuery || query;
        const result = await drawingService.getDrawings(queryToUse, projectId);

        if (result.success) {
          setDrawings(result.drawings);
          setCurrentPage(result.page);
          setTotalPages(result.totalPages);
          setTotalDrawings(result.total);
        } else {
          toast.error('Failed to load drawings');
          setDrawings([]);
        }
      } catch (error) {
        console.error('Failed to load drawings:', error);
        toast.error('Failed to load drawings');
        setDrawings([]);
      } finally {
        setLoading(false);
      }
    },
    [projectId, toast]
  );

  const loadStats = useCallback(async () => {
    try {
      await drawingService.getDrawingStats(projectId);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, [projectId]);

  const loadConfigurations = useCallback(async () => {
    try {
      setLoadingConfigs(true);
      const [configs, projectList] = await Promise.all([
        drawingService.getDrawingConfigurations(),
        showProject ? drawingService.getProjectsForDropdown() : Promise.resolve([]),
      ]);

      setCategories(configs.categories);
      setStatuses(configs.statuses);
      setDisciplines(configs.disciplines);
      setPhases(configs.phases);
      setSortOptions(configs.sortOptions);
      setProjects(projectList);
    } catch (error) {
      console.error('Failed to load configurations:', error);
    } finally {
      setLoadingConfigs(false);
    }
  }, [showProject]);

  // Effects
  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  useEffect(() => {
    loadDrawings(query);
    if (!compact) {
      loadStats();
    }
  }, [projectId, compact]); // Remove problematic dependencies

  // Separate effect for query changes
  useEffect(() => {
    loadDrawings(query);
  }, [query]);

  // Handlers
  const handleQueryChange = useCallback(
    (newQuery: DrawingQuery) => {
      const hasChanged = JSON.stringify(query) !== JSON.stringify(newQuery);
      if (hasChanged) {
        setSelectedDrawings(new Set()); // Clear selection when filters change
        setQuery(newQuery);
      }
    },
    [query]
  );

  const handlePageChange = useCallback((page: number) => {
    setQuery(prevQuery => ({ ...prevQuery, page }));
  }, []);

  const handleDrawingSelect = (drawingId: string) => {
    const newSelected = new Set(selectedDrawings);
    if (newSelected.has(drawingId)) {
      newSelected.delete(drawingId);
    } else {
      newSelected.add(drawingId);
    }
    setSelectedDrawings(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDrawings.size === drawings.length) {
      setSelectedDrawings(new Set());
    } else {
      setSelectedDrawings(new Set(drawings.map(d => d._id)));
    }
  };

  const handleDrawingView = (drawing: ProjectDrawing) => {
    if (onDrawingSelect) {
      onDrawingSelect(drawing);
    } else {
      setSelectedDrawing(drawing);
      setShowViewer(true);
    }
  };

  const handleDrawingEdit = (drawing: ProjectDrawing) => {
    setEditingDrawing(drawing);
    setShowEditModal(true);
  };

  const handleDrawingDownload = async (drawing: ProjectDrawing) => {
    try {
      await drawingService.downloadDrawing(drawing._id, drawing.file_info.original_name, projectId);
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download drawing');
    }
  };

  const handleDrawingDelete = async (drawing: ProjectDrawing) => {
    if (!window.confirm(`Are you sure you want to delete "${drawing.name}"?`)) {
      return;
    }

    try {
      const result = await drawingService.deleteDrawing(drawing._id, projectId);
      if (result.success) {
        toast.success('Drawing deleted successfully');
        loadDrawings();
        loadStats();
      } else {
        toast.error(result.message || 'Failed to delete drawing');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete drawing');
    }
  };

  const handleDrawingComment = (drawing: ProjectDrawing) => {
    setSelectedDrawing(drawing);
    setShowViewer(true);
  };

  // Bulk operations
  const handleBulkDelete = async () => {
    if (selectedDrawings.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedDrawings.size} drawing(s)?`)) {
      return;
    }

    try {
      const operation: DrawingBulkOperation = {
        action: 'delete',
        drawing_ids: Array.from(selectedDrawings),
      };

      const result = await drawingService.bulkDrawingOperation(operation, projectId);
      if (result.success) {
        toast.success(`${selectedDrawings.size} drawing(s) deleted successfully`);
        setSelectedDrawings(new Set());
        loadDrawings();
        loadStats();
      } else {
        toast.error(result.message || 'Failed to delete drawings');
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete drawings');
    }
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedDrawings.size === 0) return;

    try {
      const operation: DrawingBulkOperation = {
        action: 'update_status',
        drawing_ids: Array.from(selectedDrawings),
        data: { status: status as any },
      };

      const result = await drawingService.bulkDrawingOperation(operation, projectId);
      if (result.success) {
        toast.success(`${selectedDrawings.size} drawing(s) status updated`);
        setSelectedDrawings(new Set());
        loadDrawings();
        loadStats();
      } else {
        toast.error(result.message || 'Failed to update drawings');
      }
    } catch (error) {
      console.error('Bulk status update failed:', error);
      toast.error('Failed to update drawings');
    }
  };

  const handleUploadSuccess = useCallback(() => {
    loadDrawings();
    if (!compact) {
      loadStats();
    }
  }, [loadDrawings, loadStats, compact]);

  const handleEditSuccess = useCallback(() => {
    loadDrawings();
    if (!compact) {
      loadStats();
    }
    setEditingDrawing(null);
  }, [loadDrawings, loadStats, compact]);

  const handleRefresh = useCallback(() => {
    loadDrawings();
    if (!compact) {
      loadStats();
    }
  }, [loadDrawings, loadStats, compact]);

  if (loading && drawings.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {projectName ? `${projectName} Drawings` : 'Drawings'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {totalDrawings} drawing{totalDrawings !== 1 ? 's' : ''} found
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>

            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Grid View"
              >
                <Grid3X3 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="List View"
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {!compact && (
        <DrawingFilters
          query={query}
          onQueryChange={handleQueryChange}
          projects={projects}
          categories={categories}
          statuses={statuses}
          disciplines={disciplines}
          phases={phases}
          sortOptions={sortOptions}
          showProject={showProject}
          loading={loadingConfigs}
        />
      )}

      {/* Bulk Actions */}
      {selectedDrawings.size > 0 && (
        <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSelectAll}
                className="p-1 hover:bg-brand-100 rounded transition-colors"
              >
                {selectedDrawings.size === drawings.length ? (
                  <CheckSquare className="h-4 w-4 text-brand-primary" />
                ) : (
                  <Square className="h-4 w-4 text-brand-primary" />
                )}
              </button>
              <span className="text-sm font-medium text-brand-900">
                {selectedDrawings.size} selected
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <select
                onChange={e => {
                  if (e.target.value) {
                    handleBulkStatusUpdate(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="text-sm bg-white border border-gray-300 rounded px-3 py-1.5 text-gray-700"
                defaultValue=""
              >
                <option value="" disabled>
                  Update Status
                </option>
                {statuses.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>

              <button
                onClick={handleBulkDelete}
                className="flex items-center px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </button>

              <button
                onClick={() => setSelectedDrawings(new Set())}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawings Grid/List */}
      {drawings.length === 0 ? (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileImage className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">No drawings found</h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              {query.search || Object.keys(query).length > 3
                ? "Try adjusting your search criteria or filters to find what you're looking for."
                : `Get started by uploading your first drawing${projectName ? ` to ${projectName}` : ''}. You can upload PDF, DWG, DXF, and image files.`}
            </p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload Your First Drawing
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Results */}
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
                : 'space-y-2'
            }
          >
            {drawings.map(drawing => (
              <DrawingCard
                key={drawing._id}
                drawing={drawing}
                viewMode={viewMode}
                isSelected={selectedDrawings.has(drawing._id)}
                onSelect={!compact ? handleDrawingSelect : undefined}
                onView={handleDrawingView}
                onEdit={handleDrawingEdit}
                onDownload={handleDrawingDownload}
                onDelete={handleDrawingDelete}
                onComment={handleDrawingComment}
                showProject={showProject}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalDrawings}
                itemsPerPage={query.limit || 20}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}

      {/* Upload Drawer */}
      <DrawingFormDrawer
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={handleUploadSuccess}
        projectId={projectId}
        projects={projects}
        mode="create"
      />

      {/* Edit Drawer */}
      <DrawingFormDrawer
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingDrawing(null);
        }}
        onSuccess={handleEditSuccess}
        projectId={projectId}
        projects={projects}
        editingDrawing={editingDrawing}
        mode="edit"
      />

      {/* Drawing Viewer */}
      <DrawingViewer
        drawing={selectedDrawing}
        isOpen={showViewer}
        onClose={() => {
          setShowViewer(false);
          setSelectedDrawing(null);
        }}
        onDownload={selectedDrawing ? () => handleDrawingDownload(selectedDrawing) : undefined}
        onStatusChange={(_, newStatus) => {
          // Handle status change
          console.log('Status change:', newStatus);
          // Reload drawings after status change
          loadDrawings();
          if (!compact) {
            loadStats();
          }
        }}
        canEdit={true} // This should be based on user permissions
        canApprove={true} // This should be based on user role
      />
    </div>
  );
};

export default DrawingManagement;
